<template>
  <div dir="rtl" class="flex flex-col gap-4 overflow-y-auto relative">
    <PVSelectButton
      v-model="activeSource"
      :pt="selectButtonPT"
      :options="sourceOptions"
      option-label="label"
      option-value="value"
      class="w-full flex">
      <template #option="{ option }">
        <div class="flex gap-1.5 items-center justify-center">
          <component
            :is="option.icon"
            :size="12"
            class="stroke-secondary-800"
            :class="{ 'stroke-secondary-900': option.value === activeSource }"/>
          <p
            class="text-secondary-800 font-simplerBold text-xs"
            :class="{ '!text-secondary-900': option.value === activeSource }">
            {{ option.label }}
          </p>
        </div>
      </template>
    </PVSelectButton>
    <div class="flex h-[300px]">
      <ScrollableContainer>
        <ImageUnsplash
          v-show="activeSource === 'unsplash'"
          v-model:src="imageSrc"
          v-model:alt="imageAlt"/>
        <ImageGenerator
          v-show="activeSource === 'ai'"
          :id="id"
          v-model:src="imageSrc"
          v-model:alt="imageAlt"/>
        <FileInput
          v-show="activeSource === 'upload'"
          v-model="imageSrc"
          :label="t('blocksSettings.image.upload_image_computer')"
          :block-id="id"
          class="w-full"
          type="image"
          @upload="uploadCallback"/>
      </ScrollableContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Image, WandSparkles, Upload } from 'lucide-vue-next';
import FileInput from './FileInput/FileInput.vue';
import PVSelectButton from 'primevue/selectbutton';
import ImageGenerator from './ImageGenerator/ImageGenerator.vue';
import ImageUnsplash from './ImageUnsplash/ImageUnsplash.vue';
import { ScrollableContainer } from '@nwire/amit-design-system';

const { t } = useI18n();

const activeSource = ref('unsplash');

defineProps<{
  id: string;
}>();

const imageSrc = defineModel<string>('src', { required: true });
const imageAlt = defineModel<string>('alt', { required: false });

const sourceOptions = [
  { label: t('blocksSettings.image.unsplash'), value: 'unsplash', icon: Image },
  { label: 'AI', value: 'ai', icon: WandSparkles },
  { label: t('blocksSettings.image.upload'), value: 'upload', icon: Upload },
];

const selectButtonPT = {
  pcToggleButton: {
    root: ({ context }) => ({
      class: [
        'relative',
        'leading-none',
        'inline-flex items-center justify-center',
        'flex-1',
        'py-2 border border-secondary-250 first:rounded-s-md last:rounded-e-md bg-white',

        {
          'border-1 border-secondary-900': context.active,
        },

        {
          'opacity-60 select-none pointer-events-none cursor-default':
            context.disabled,
        },

        // Transition
        'transition duration-200',
        'hover:bg-secondary-75 hover:border-secondary-900',

        // Misc
        'cursor-pointer select-none overflow-hidden',
      ],
    }),
  },
};

const uploadCallback = (result: string) => {
  imageSrc.value = result;
};
</script>
<style scoped>
.chess {
  background: repeating-conic-gradient(#fff 0 90deg, grey 0 180deg) 0 0/10px
    10px;
  padding: 10px;
}
</style>
